import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { customerTenant, environment } from '@env/environment';
import {
    DeepPartial,
    IAccount,
    ICard,
    ICardStatusResponse,
    IChangeCustomerStatusApiRequest,
    ICreateAccount,
    ICreateCustomer,
    ICustomer,
    ICustomerAnalyticsApiResponse,
    ICustomerCountApiResponse,
    ICustomerIdentification,
    ICustomerIdentificationFile,
    ICustomerIdentificationFlattenedFile,
    ICustomerSearchRequest,
    IIdentificationDocument,
    IInternationalFlag,
    IKycReport,
    IKycReportListing,
    IListResponse,
    IMobileHistory,
    IActivityLog,
    sdkToken,
    UserType,
    IActivityLogFilter,
    IIdvDuplicationMatchesResponse,
} from '@lib/interfaces';
import { map, Observable, switchMap } from 'rxjs';
import { Utils } from '@lib/utils';
import { IdvReports } from '@lib/interfaces/user-idv-report.interface';
import { TenantService } from '../tenant.service';
import { CustomerStatusCode } from '@lib/constants';

@Injectable({
    providedIn: 'root',
})
export class CustomersService {
    public environment_url: string;
    tenantKey: string;

    headers = new HttpHeaders({
        'x-nymos-idempotency-key': crypto.randomUUID(),
        'x-no-authorization': 'true',
    });
    public constructor(private http: HttpClient, private utils: Utils, private tenantService: TenantService) {
        this.environment_url = environment.api_url;
    }

    private random3DigitNumber(): number {
        return Math.floor(Math.random() * 900) + 100;
    }

    public createCustomer(payload: DeepPartial<ICreateCustomer>): Observable<ICustomer> {
        return this.http.post<ICustomer>(
            `${this.environment_url}nfront/v1/proxy/users`,
            {
                user_type: 'INDIVIDUAL',
                company_name: 'Example',
                ...payload,
            },
            {
                headers: new HttpHeaders({
                    Apikey: this.tenantService.getTenantKey() || '',
                }),
            },
        );
    }

    public createAgent(payload: DeepPartial<ICreateCustomer>, currencies = ['USD', 'SDG', 'GBP', 'EUR', 'SAR', 'AED']): Observable<ICustomer> {
        if (payload.address?.postal_code) {
            payload.address.postal_code = String(payload.address?.postal_code);
        }
        return this.http.post<ICustomer>(`${this.environment_url}nfront/v1/proxy/users`, {
            user_type: UserType.AGENT,
            preferred_currencies: currencies,
            ...payload,
        });
    }

    public createAgentINC(payload: DeepPartial<ICreateCustomer>, currencies = ['IQD']): Observable<ICustomer> {
        if (payload.address?.postal_code) {
            payload.address.postal_code = String(payload.address?.postal_code);
        }
        return this.http.post<ICustomer>(`${this.environment_url}nfront/v1/proxy/agents`, {
            user_type: UserType.AGENT,
            preferred_currencies: currencies,
            ...payload,
        });
    }

    public createIdentification(payload: any): Observable<any> {
        return this.http.post<any>(
            `${this.environment_url}nfront/v1/proxy/users/35d170a4-ab5c-4016-a493-6c3e8c5c25e0/identifications`,
            { ...payload },
            {
                headers: new HttpHeaders({
                    Apikey: this.tenantService.getTenantKey() || '',
                }),
            },
        );
    }

    public uploadIdentification(user_id: string, id: string, payload: any): Observable<any> {
        return this.http.post<any>(`${this.environment_url}nfront/v1/proxy/users/${user_id}/identifications/${id}/files`, payload, {
            headers: new HttpHeaders({
                Apikey: this.tenantService.getTenantKey() || '',
            }),
        });
    }

    public fetchCustomerAvatar(id: string): Observable<any> {
        return this.http.get<any>(`${this.environment_url}v1/users/${id}/avatar`, {
            headers: new HttpHeaders({
                Apikey: this.tenantService.getTenantKey() || '',
                'x-no-authorization': 'true',
            }),
        });
    }

    public uploadCustomerAvatar(id: string): Observable<any> {
        return this.http.post<any>(
            `${this.environment_url}v1/users/${id}/avatar`,
            {},
            {
                headers: new HttpHeaders({
                    Apikey: this.tenantService.getTenantKey() || '',
                    'x-no-authorization': 'true',
                }),
            },
        );
    }

    public searchCustomer(searchInput: string): Observable<IListResponse<ICustomer>> {
        return this.http.post<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/users:search`, {
            mobile: searchInput,
            after: null,
            limit: 10,
            order_by: 'desc',
            sort_by: 'created',
        });
    }

    public filterCustomersByUserId(payload: DeepPartial<ICustomerSearchRequest>): Observable<IListResponse<ICustomer>> {
        return this.http.post<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/users/fetch`, payload);
    }

    public filterCustomersByPan(hashPan: string): Observable<IListResponse<ICustomer>> {
        return this.http.get<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/users/lookup/byCardHashedPan?hashed_pan=${hashPan}`);
    }

    public filterCustomers(payload: DeepPartial<ICustomerSearchRequest>): Observable<IListResponse<ICustomer>> {
        return this.http.post<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/users:search`, payload);
    }
    public filterAgents(payload: DeepPartial<ICustomerSearchRequest>): Observable<IListResponse<ICustomer>> {
        return this.http.post<IListResponse<ICustomer>>(`${this.environment_url}nfront/v1/proxy/agents:search`, payload);
    }
    public getMobileHistroyByFilters(params:HttpParams): Observable<IListResponse<IMobileHistory>> {
       
        return this.http.get<IListResponse<IMobileHistory>>(`${this.environment_url}nfront/v1/mobile-audit`, { params });
    }

    public filterCustomersMultiTenant(payload: string): Observable<IListResponse<ICustomer>> {
        return this.http.get<IListResponse<ICustomer>>(`${this.environment_url}internal/v1/admins/users/multitenant:search?${payload}`, {
            headers: this.headers,
        });
    }

    public fetchCustomerDetailsById(customerId: string): Observable<ICustomer> {
        return this.http.get<ICustomer>(`${this.environment_url}nfront/v1/proxy/users/${customerId}`);
    }

    public fetchCustomerDetailsByParentId(customerId: string): Observable<any> {
        return this.http.get<ICustomer>(`${this.environment_url}nfront/v1/proxy/users?parent_user_id=${customerId}`);
    }

    public updateCustomerDetailsById(customerId: string, payload: DeepPartial<ICustomer>): Observable<ICustomer> {
        return this.http.put<ICustomer>(`${this.environment_url}nfront/v1/proxy/users/${customerId}`, payload);
    }

    public changeCustomerStatus(customerId: string, payload: IChangeCustomerStatusApiRequest): Observable<ICustomer> {
        const tenant = this.tenantService.getTenantKey();
        const statusReasonCodeDescription = CustomerStatusCode[payload.status_reason_code as keyof typeof CustomerStatusCode];
        const userType = statusReasonCodeDescription?.includes('Admin') ? 'admins/users' : 'users';
        const isChangedStatusActiveSuspendedOrTerminated = ['ACTIVE', 'SUSPENDED', 'TERMINATED'].includes(payload.status);

        if (tenant?.split('_')[0] === customerTenant.INC) {
            const action = isChangedStatusActiveSuspendedOrTerminated ? 'updateStatus' : 'changeStatus';

            return this.http.put<ICustomer>(`${this.environment_url}nfront/v1/proxy/${userType}/${customerId}:${action}`, payload);
        }

        return this.http.put<ICustomer>(`${this.environment_url}nfront/v1/proxy/${userType}/${customerId}:changeStatus`, payload);
    }

    public checkCustomerT24Status(customerId: string): Observable<ICustomer> {
        return this.http.post<ICustomer>(`${this.environment_url}nfront/v1/proxy/cardless/customers/${customerId}/validate`, {});
    }

    public changeCustomerKYCStatus(customerId: string, payload: any): Observable<IKycReport> {
        return this.http.post<IKycReport>(`${this.environment_url}nfront/v1/proxy/users/${customerId}/kyc`, payload);
    }

    public processKYCReport(customerId: string): Observable<IKycReport> {
        return this.http.post<IKycReport>(`${this.environment_url}nfront/v1/proxy/users/${customerId}/kyc:reprocess`, {});
    }

    public getKYCReport(queryParams: any): Observable<IKycReportListing> {
        return this.http.post<IKycReportListing>(`${this.environment_url}nfront/v1/proxy/users/kyc:search`,  queryParams);
    }

    public getCustomerKYCReport(customerId: string): Observable<IKycReportListing> {
        return this.http.get<IKycReportListing>(`${this.environment_url}nfront/v1/proxy/users/kyc/${customerId}/details`);
    }

    public fetchCustomerCards(customerId: string, limit = 10): Observable<IListResponse<ICard>> {
        return this.http.get<IListResponse<ICard>>(`${this.environment_url}nfront/v1/proxy/cards?user_id=${customerId}&limit=${limit}`);
    }

    public fetchCustomerCardsByTenant(tenantId: string, customerId: string, limit = 10): Observable<IListResponse<ICard>> {
        const headers = new HttpHeaders({
            'x-nymos-idempotency-key': crypto.randomUUID(),
            'x-no-authorization': 'true',
        });
        return this.http.get<IListResponse<ICard>>(`${this.environment_url}internal/v1/admins/cards?userId=${customerId}&tenantId=${tenantId}&limit=${limit}&after=0`, {
            headers: headers,
        });
    }

    public fetchCustomerCardsForLazyLoading(customerId: string, payload: any): Observable<IListResponse<ICard>> {
        return this.http.get<IListResponse<ICard>>(`${this.environment_url}nfront/v1/proxy/cards?user_id=${customerId}&limit=${payload.limit}&after=${payload.after}`);
    }

    public fetchCustomerAccounts(customerId: string, limit = 50, after = '', queryParam = ''): Observable<IListResponse<IAccount>> {
        return this.http.get<IListResponse<IAccount>>(`${this.environment_url}nfront/v1/proxy/accounts?user_id=${customerId}&limit=${limit}&after=${after}&${queryParam}`);
    }

    public pushDataCustomerStatus(customerId: string): Observable<any> {
        return this.http.post<any>(`${this.environment_url}nfront/v1/cardless/customers/${customerId}`, {});
    }

    public changeCardStatus(id: string, card: any): Observable<ICardStatusResponse> {
        return this.http.put<ICardStatusResponse>(`${this.environment_url}nfront/v1/proxy/cards/${id}/statuschange`, card);
    }

    public createAccount(accountDetails: DeepPartial<ICreateAccount>): Observable<IAccount> {
        return this.http.post<IAccount>(`${this.environment_url}nfront/v1/proxy/accounts`, accountDetails);
    }

    public fetchCustomerIdentificationFiles(userId: string): Observable<Array<ICustomerIdentificationFlattenedFile>> {
        return this.http.get<Array<ICustomerIdentification>>(`${this.environment_url}nfront/v1/proxy/users/${userId}/identifications`).pipe(
            map((identifications: Array<ICustomerIdentification>) =>
                identifications.flatMap((identification: ICustomerIdentification): Array<ICustomerIdentificationFlattenedFile> => {
                    return identification.files.map(
                        (file: ICustomerIdentificationFile): ICustomerIdentificationFlattenedFile => ({
                            ...file,
                            identification: identification,
                        }),
                    );
                }),
            ),
        );
    }

    public fetchCustomerIdentificationLiveLinessVideoFiles(userId: string): Observable<any> {
        const tenant = this.tenantService.getTenantKey();

        if (tenant?.split('_')[0] === customerTenant.INC) {
            return this.http.get(`${this.environment_url}nfront/v2/proxy/identityverifications/users/${userId}/documents?document_type=liveliness_video&flow_type=ONBOARDING`);
        }

        return this.http.get(`${this.environment_url}nfront/v1/proxy/identityverifications/users/${userId}/documents/liveness_video`);
    }

    public createCustomerIdentification(userId: string, payload: IIdentificationDocument): Observable<IIdentificationDocument> {
        return this.http.post<IIdentificationDocument>(`${this.environment_url}nfront/v1/proxy/users/${userId}/identifications`, payload);
    }

    public updateCustomerIdentification(userId: string, identificationId: string, payload: FormData): Observable<IIdentificationDocument> {
        return this.http.put<IIdentificationDocument>(`${this.environment_url}nfront/v1/proxy/users/${userId}/identifications/${identificationId}/files`, payload);
    }

    public uploadCustomerIdentificationDocuments(userId: string, identificationId: string, payload: FormData) {
        return this.http.post(`${this.environment_url}nfront/v1/proxy/users/${userId}/identifications/${identificationId}/files`, payload);
    }

    public fetchCustomerIdentificationDocumentFile(userId: string, identificationId: string, fileId: string): Observable<Blob> {
        return this.http.get<Blob>(`${this.environment_url}nfront/v1/proxy/users/${userId}/identifications/${identificationId}/files/${fileId}:browse`, {
            responseType: 'blob' as 'json',
        });
    }

    public fetchCustomerAnalytics(): Observable<ICustomerAnalyticsApiResponse> {
        const tenantKey = this.tenantService.getTenantKey() || '';
        return this.http.get<ICustomerAnalyticsApiResponse>(`${this.environment_url}nfront/v1/proxy/admins/user:analytics?duration=week&tenant=${tenantKey}`);
    }

    public fetchCustomerCount(): Observable<ICustomerCountApiResponse> {
        const tenantKey = this.tenantService.getTenantKey() || '';
        return this.http.get<ICustomerCountApiResponse>(`${this.environment_url}nfront/v1/proxy/admins/user:count?duration=week&tenant=${tenantKey}`);
    }

    public fetchCustomerAnalyticsMonthly(): Observable<ICustomerAnalyticsApiResponse> {
        const tenantKey = this.tenantService.getTenantKey() || '';
        return this.http.get<ICustomerAnalyticsApiResponse>(`${this.environment_url}nfront/v1/proxy/admins/user:analytics?duration=month&tenant=${tenantKey}`);
    }

    public fetchCustomerCountMonthly(): Observable<ICustomerCountApiResponse> {
        const tenantKey = this.tenantService.getTenantKey() || '';
        return this.http.get<ICustomerCountApiResponse>(`${this.environment_url}nfront/v1/proxy/admins/user:count?duration=month&tenant=${tenantKey}`);
    }

    public fetchCustomerIdvReports(userId: string) {
        return this.http.get<IdvReports>(`${this.environment_url}nfront/v1/proxy/identityverifications/reports/users/${userId}`);
    }

    public fetchIdvDuplicationMatches(userId: string): Observable<IIdvDuplicationMatchesResponse> {
        return this.http.get<IIdvDuplicationMatchesResponse>(`${this.environment_url}nfront/v1/proxy/users/${userId}/duplication-matches`);
    }

    public generateSDKToken(userId: string) {
        return this.http.post<sdkToken>(`${this.environment_url}nfront/v1/proxy/identityverifications/sdkToken`, {
            user_id: userId,
        });
    }

    public fetchInternationFlag(userId: string) {
        return this.http.get<IInternationalFlag>(`${this.environment_url}nfront/v1/proxy/users/${userId}:checkInternationalTransactions`);
    }

    public updateInternationFlag(userId: string, payload: IInternationalFlag) {
        return this.http.put<IInternationalFlag>(`${this.environment_url}nfront/v1/proxy/users/${userId}:checkInternationalTransactions`, payload);
    }

    public getActivityLog(payload: IActivityLogFilter): Observable<IListResponse<IActivityLog>> {
        return this.http.post<IListResponse<IActivityLog>>(`${this.environment_url}nfront/v1/admins/auditlogs:search`, { ...payload });
    }

    public exportActivityLog(filters: any, selectedColumns: string[]): Observable<Blob> {
        return this.http.post(
            `${this.environment_url}nfront/v1/admins/auditlogs:export?format=XLSX`,
            { ...filters, selected_columns: selectedColumns },
            {
                headers: new HttpHeaders({
                    'content-type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                }),
                responseType: 'blob',
            },
        );
    }


    public fetchCustomerIBANDetailsById(customerId: string): Observable<ICustomer> { 
        return this.http.post<ICustomer>(`${this.environment_url}nfront/v1/proxy/cardless/customers/${customerId}/session`, {}, {
            headers: new HttpHeaders({
                'x-nfront-device-id': this.random3DigitNumber()
            }),
        });
    }
}
